import React, { useEffect, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Post, Get } from "../../Axios/AxiosFunctions";
import { Button } from "../../Component/Button/Button";
import Footer from "../../Component/Footer";
import Header from "../../Component/Header";
import { apiHeader, BaseURL, imageUrl } from "../../config/apiUrl";
import classes from "./CompanyNDAForm.module.css";
import moment from "moment-timezone";
import { formatPhoneNumber } from "../../utils/functions";
import { Loader } from "../../Component/Loader";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import jstz from "jstz";
import "dayjs/locale/en";

const advancedFormat = require("dayjs/plugin/advancedFormat");

const formateDate = (date) => {
  return `${dayjs(new Date(date))?.format("MM-DD-YYYY")}`;
};

const getTimezoneName = () => {
  const timeZone = jstz.determine();
  return timeZone.name();
};

const formateCurrentDateAndTime = () => {
  dayjs.extend(utc);
  dayjs.extend(timezone);
  dayjs.locale("en");
  dayjs?.extend(advancedFormat);

  return `${dayjs(new Date())
    .tz(getTimezoneName())
    .format("ddd, DD MMM YYYY [at] hh:mm A z")}`;
};

function CompanyNDAForm() {
  const formRef = useRef(null);

  const {
    user,
    accessToken,
    company_id: companyId,
  } = useSelector((state) => state.authReducer);

  const navigate = useNavigate();
  const location = useLocation();
  const refferedBusinessId = location.state?.refId;
  const headline = location.state?.headline;
  const businessId = location.state?.businessId;
  const refId = location.state?.listingId;

  const queryParams = new URLSearchParams(location.search);
  const listing = queryParams.get("listing");

  // const [contactPhoneNumber, setContactPhoneNumber] = useState('');
  const storedPhone = localStorage.getItem("frontend_companyPhone");

  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(null);
  const [listingData, setListingData] = useState(null);

  // Personal Info
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipcode, setZipcode] = useState("");
  // Broker Info
  const [licensedBroker, setLicensedBroker] = useState("No");
  const [nameOfBroker, setNameOfBroker] = useState("");
  const [brokerCompanyName, setBrokerCompanyName] = useState("");
  // Location Preference
  const [locationPreference, setLocationPreference] = useState("");
  const [capitalAvailable, setCapitalAvailable] = useState("");
  const [currentOccupation, setCurrentOccupation] = useState("");
  const [typesOfBusiness, setTypesOfBusiness] = useState("");
  const [timeAlloc, setTimeAlloc] = useState("");
  const [minAnnualIncome, setMinAnnualIncome] = useState("");
  const [address, setAddress] = useState("");

  const [isSending, setIsSending] = useState(false);
  const [ip, setIp] = useState("");

  const baseApiUrl = BaseURL(`company-nda-template`);

  const getCompanyTemplate = async () => {
    try {
      const response = await Get(baseApiUrl, accessToken, companyId);

      if (response?.status === 200 && response?.data) {
        setData(response?.data);
        setIsLoading(false);
      } else {
        navigate(-1);
      }
    } catch {
      setData(null);
      setIsLoading(false);
    }
  };

  const fetchListingData = async () => {
    setIsLoading(true);

    const url = BaseURL(`business/general/${listing}`);

    const response = await Get(url, accessToken, companyId);

    if (response !== undefined) {
      const resData = response?.data?.data;
      setListingData(resData);
    } else {
      navigate(-1);
    }

    setIsLoading(false);
  };

  useEffect(() => {
    if (accessToken) {
      getCompanyTemplate();
      fetchListingData();
    }
  }, [accessToken]);

  const getAgreementContent = (content, isPdf = false) => {
    if (content) {
      let replacedContent = `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta http-equiv="X-UA-Compatible" content="IE=edge" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>Confidentiality Agreement</title>
    <link
      href="https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap"
      rel="stylesheet"
    />
    <style>

  .signature {
    font-family: 'Great Vibes', cursive;
    font-weight: bold;
  }
    </style>
  </head>

  <body>
    <div style="margin:0px 40px;">${content}</div>
  </body>
</html>`;

      const companyLogo = `${imageUrl}${data?.company?.logoUrl}`;

      replacedContent = replacedContent?.replaceAll(
        "*|company_logo|*",
        `
              <img style="
                width: 200px;
                height: 80px;
                object-fit: contain;
                object-position: center;
              " src="${companyLogo || ""}" alt="logo" />`
      );

      replacedContent = replacedContent?.replaceAll(
        "*|brokerage_name|*",
        data?.company?.name || ""
      );

      replacedContent = replacedContent?.replaceAll(
        "*|buyer_name|*",
        `${user?.firstName || ""} ${user?.lastName || ""}`
      );

      replacedContent = replacedContent?.replaceAll(
        "*|signature_box|*",
        `
            <span
            style= "font-weight:bold; font-family:Great Vibes, cursive"
              
            >
            ${user?.firstName || ""} ${user?.lastName || ""}
            </span>`
      );

      replacedContent = replacedContent?.replaceAll(
        "*|buyer_email|*",
        user?.email || ""
      );

      replacedContent = replacedContent?.replaceAll(
        "*|buyer_phone|*",
        user?.contact ? formatPhoneNumber(user?.contact) : ""
      );

      replacedContent = replacedContent?.replaceAll(
        "*|listing_reference_id|*",
        listingData?.refId || ""
      );

      replacedContent = replacedContent?.replaceAll(
        "*|listing_headline|*",
        listingData?.title || ""
      );

      const listing_broker_name = listingData?.broker?.length
        ? `${listingData?.broker?.[0]?.firstName || ""} ${
            listingData?.broker?.[0]?.lastName || ""
          }`
        : "";

      replacedContent = replacedContent?.replaceAll(
        "*|listing_broker_name|*",
        listing_broker_name || ""
      );

      replacedContent = replacedContent?.replaceAll(
        "*|today_date|*",
        formateDate(new Date()) || ""
      );

      replacedContent = replacedContent?.replaceAll(
        "*|today_date_and_time|*",
        formateCurrentDateAndTime() || ""
      );

      if (isPdf) {
        // Checkbox
        replacedContent = replacedContent?.replaceAll("*|[ ]|*", "");

        // inputs
        let index = 1;

        replacedContent = replacedContent?.replaceAll("*|input_box|*", () => {
          const inputId = `input_${index}`;

          const input = document?.getElementById(inputId)?.value || "-";
          index++;

          return input;
        });

        replacedContent = replacedContent?.replaceAll(
          "*|choose_one_option|*",
          () => {
            let value = "";
            const element = document?.querySelector(".chooseOneOption");
            element
              ?.querySelectorAll(".choose_one_option_item")
              .forEach((input) => {
                if (input?.checked) {
                  value = input?.value || "";
                }
              });

            return `<p style="color:black; font-weight:600; font-size:17px; margin-top:-10px">${value}</p>`;
          }
        );
      } else {
        // Checkboxes
        let checkBoxIndex = 1;
        replacedContent = replacedContent?.replaceAll("*|[ ]|*", () => {
          const checkBoxId = `checkbox_${checkBoxIndex}`;

          checkBoxIndex++;

          return `<p class="check_box_container">
        <input type="checkbox" name=${checkBoxId} id=${checkBoxId} required>  
        <span class="checkmark checkmark_event" id="checkmarkId${checkBoxId}"></span>
          </p>`;
        });

        // Inputs
        let index = 1;

        replacedContent = replacedContent?.replaceAll("*|input_box|*", () => {
          const inputId = `input_${index}`;

          index++;

          return `<input type="text" id="${inputId}" name="${inputId}" style="border:1px solid gray; padding:16px; width:100%; border-radius:6px; font-size:16px; font-weight:500 !important; color:inherit">`;
        });

        // Choose One Option
        replacedContent = replacedContent?.replaceAll(
          "*|choose_one_option|*",

          () => {
            return `<div class="chooseOneOption">
  <div class="chooseOneOptionItem">
    <label for="Yes">Yes</label>
    <p class="check_box_container">
      <input class="choose_one_option_item" checked type="radio" name="choose_one_option" id="option_1" value="Yes" required>
      <span class="checkmark choose_one_option"></span>
    </p>
  </div>

  <div class="chooseOneOptionItem">
    <label for="No">No</label>
    <p class="check_box_container">
      <input class="choose_one_option_item" type="radio" name="choose_one_option" id="option_2" value="No" required>
      <span class="checkmark choose_one_option"></span>
    </p>
  </div>
</div>`;
          }
        );
      }

      return replacedContent;
    } else {
      return "";
    }
  };

  useEffect(() => {
    fetch("https://api.ipify.org?format=json")
      .then((response) => response.json())
      .then((data) => setIp(data?.ip))
      .catch((error) => console.error(error));
  }, []);

  const handleSubmit = async (event) => {
    event?.preventDefault();

    const formElements = formRef.current;

    let formData = {};

    let formIsValid = true;

    let isAgreeToAllTearm = true;

    const isCheckFieldsFillUp = () => {
      let isValid = true;
      for (let element of formElements) {
        if (
          element?.name &&
          element?.type === "text" &&
          !element.value?.trim()
        ) {
          isValid = false;
        }
      }

      return isValid;
    };

    for (let element of formElements) {
      if (element?.name) {
        if (
          element.type === "checkbox" &&
          element.required &&
          !element.checked
        ) {
          isAgreeToAllTearm = false;
        }

        if (
          element.type === "radio" &&
          element?.name === "choose_one_option" &&
          element.checked &&
          element?.value === "Yes" &&
          !isCheckFieldsFillUp()
        ) {
          formIsValid = false;
        }

        formData[element.name] = element.value;
      }
    }

    if (!formIsValid) {
      toast.error("All Fields are required");
    } else if (!isAgreeToAllTearm) {
      toast.error("Please agree to all terms");
    } else {
      setIsSending(true);

      const url = BaseURL("leads/sign-nda");

      const userTimezone = moment?.tz?.guess();
      const now = moment()?.tz(userTimezone);
      const formattedDate = now.format("ddd, DD MMM YYYY [at] h:mm A z");

      const params = {
        firstName: user?.firstName,
        lastName: user?.lastName,
        contact: user?.contact,
        email: user?.email,
        refferedBusiness: refferedBusinessId,
        headline: headline,
        businessId,
        ...(licensedBroker == "Yes" && {
          brokerName: nameOfBroker,
          brokerCompanyName,
        }),
        areYouSure: true,
        areYouAcknowledged: true,
        currentOccupation,
        refId: refId,
        businessInterested: typesOfBusiness,
        minAnnualIncomeNeeds: minAnnualIncome,
        licensedBroker: licensedBroker == "Yes" ? true : false,
        ...(licensedBroker == "No" && {
          brokerName: nameOfBroker,
          brokerCompanyName,
        }),
        timeAllocatedForBusiness: timeAlloc,
        streetAddress: address,
        city,
        state,
        zipCode: Number(zipcode),
        preferredLocation: [locationPreference],
        capitalAvailable: [capitalAvailable],
        ip,
        signing_date: formattedDate,
        data: {
          formData,
        },

        htmlContent: getAgreementContent(data?.content, true),
      };

      const response = await Post(
        url,
        params,
        apiHeader(accessToken),
        companyId
      );

      if (response !== undefined) {
        setIsSending(false);
        if (response?.data?.status === "nda-submitted") {
          toast.info(
            `Authorized broker for this listing will get back to you within 24 business hours. If you don't hear from us, please contact at ${storedPhone}.`
          );

          navigate(-1);
        } else if (response?.data?.status === "under-contract") {
          toast.info(
            `Authorized broker for this listing will get back to you within 24 business hours. If you don't hear from us, please contact at ${storedPhone}`
          );
        } else if (response?.data?.status === "under LOI") {
          toast.info(
            `Authorized broker for this listing will get back to you within 24 business hours. If you don't hear from us, please contact at ${storedPhone}`
          );
        } else {
          toast.success("Thank you for signing NDA", {
            autoClose: 10000,
          });
          navigate(-1);
        }
      } else {
        setIsSending(false);
      }
    }
  };

  useEffect(() => {
    if (!isLoading && data?.content) {
      const checkboxes = document.querySelectorAll(".checkmark_event");

      checkboxes.forEach((checkbox, index) => {
        checkbox.addEventListener("click", (event) => {
          const checkBox = document?.querySelector(`#checkbox_${index + 1}`);

          if (checkBox?.getAttribute("checked")) {
            checkBox.removeAttribute("checked");
          } else {
            checkBox.setAttribute("checked", "true");
          }
        });
      });

      const chooseOneOptions = document.querySelectorAll(".choose_one_option");

      chooseOneOptions.forEach((radio, index) => {
        radio.addEventListener("click", (event) => {
          const checkBox = document?.querySelector(`#option_${index + 1}`);

          chooseOneOptions.forEach((ratio, checkIndex) => {
            const checkBox = document?.querySelector(
              `#option_${checkIndex + 1}`
            );

            checkBox?.removeAttribute("checked");
          });

          checkBox.setAttribute("checked", "true");
        });
      });
    }
  }, [isLoading, data?.content]);

  return (
    <>
      <div className={classes.page}>
        <Header backgroundColor={"var(--white-color)"} />

        {isLoading && (
          <div>
            <Loader />
          </div>
        )}

        {!isLoading && (
          <>
            <style jsx>{`
              .table_row table {
                border-collapse: collapse;
              }

              .table_row th,
              td {
                border-width: 1px;
              }

              ul,
              li {
                margin: initial;
                list-style-type: unset;
              }
            `}</style>
            <form id="agreement_form" ref={formRef}>
              <section style={{ maxWidth: "80%", margin: "0 auto" }}>
                {data?.content && (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: getAgreementContent(data?.content),
                    }}
                  />
                )}
              </section>

              <section style={{ maxWidth: "80%", margin: "0 auto" }}>
                <Row
                  style={{
                    margin: "auto",
                    maxWidth: "700px",
                  }}
                >
                  <Col
                    md={7}
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      padding: "15px",
                      border: "1px solid black",
                    }}
                  >
                    <div>
                      <div>
                        <span style={{ fontWeight: "bold" }}>
                          Interested Party:
                        </span>
                        <span
                          style={{
                            textAlign: "justify",
                            textTransform: "capitalize",
                          }}
                        >
                          {` ${user?.firstName} ${user?.lastName}`}
                        </span>
                      </div>
                      <div>
                        <span style={{ fontWeight: "bold" }}>Email:</span>
                        <span
                          style={{
                            textAlign: "justify",
                            textDecoration: "underline",
                          }}
                        >
                          {` ${user?.email}`}
                        </span>
                      </div>
                      <div>
                        <span style={{ fontWeight: "bold" }}>Phone:</span>
                        <span style={{ textAlign: "justify" }}>
                          {` ${formatPhoneNumber(user?.contact)}`}
                        </span>
                      </div>
                      <div>
                        <span style={{ fontWeight: "bold" }}>
                          Digital Signature:
                        </span>
                        <span
                          className={classes.signature}
                          style={{
                            fontWeight: "bold",
                            display: "block",
                            fontSize: "20px",
                            marginTop: "10px",
                            fontFamily: "Great Vibes, cursive",
                          }}
                        >
                          {user?.firstName} {user?.lastName}
                        </span>
                      </div>
                    </div>
                  </Col>
                  <Col
                    md={5}
                    style={{
                      padding: "20px",
                      textAlign: "center",
                      border: "1px solid black",
                      borderLeft: "none",
                    }}
                  >
                    <Button
                      className={classes.submitBtn}
                      disabled={isSending}
                      onClick={(e) => handleSubmit(e)}
                      type="button"
                    >
                      {!isSending ? "Sign" : "Signing..."}
                    </Button>
                  </Col>
                </Row>
              </section>
            </form>
          </>
        )}

        <div className={classes.mt50}>
          <Footer />
        </div>
      </div>
    </>
  );
}

export default CompanyNDAForm;
